import apiClient from './apiClient';
import { BaseService } from './BaseService';
import { User, LoginRequest, LoginResponse, ApiResponse } from './types';

export class AuthService {
  // Login user
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      '/sign-in',
      credentials
    );

    // Store token in localStorage
    const { access_token } = response.data;
    localStorage.setItem('authToken', access_token);

    return response.data;
  }

  // Register user
  async register(userData: {
    name: string;
    email: string;
    password: string;
  }): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      '/auth/register',
      userData
    );

    // Store token in localStorage
    const { access_token } = response.data;
    localStorage.setItem('authToken', access_token);

    return response.data;
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiClient.post('/sign-out');
    } finally {
      // Always remove token from localStorage
      localStorage.removeItem('authToken');
    }
  }

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/me');
    return response.data;
  }

  // Update user profile
  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await apiClient.put<ApiResponse<User>>(
      '/auth/profile',
      userData
    );
    return response.data.data;
  }

  // Change password
  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    await apiClient.post('/auth/change-password', {
      oldPassword,
      newPassword
    });
  }

  // Forgot password
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
  }

  // Reset password
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/reset-password', {
      token,
      newPassword
    });
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }
}

// User management service
export class UserService extends BaseService<User> {
  constructor() {
    super('/users');
  }

  // Get users by role
  async getUsersByRole(role: string): Promise<User[]> {
    const response = await apiClient.get<ApiResponse<User[]>>(
      `${this.endpoint}/role/${role}`
    );
    return response.data.data;
  }

  // Activate/deactivate user
  async toggleUserStatus(id: string, active: boolean): Promise<User> {
    const response = await apiClient.patch<ApiResponse<User>>(
      `${this.endpoint}/${id}/status`,
      { active }
    );
    return response.data.data;
  }
}

// Create singleton instances
export const authService = new AuthService();
export const userService = new UserService();
